/**
 * RosePage — vista consultabile di tutte le rose della lega.
 *
 * Per ogni squadra: blocco con 25 ufficiali (lista competizione) in alto +
 * eventuali extra (provvisorie / non-ufficiali) sotto.
 * Read-only. Per la propria squadra: vai su /rosa (Gestione Rosa).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { type RosaPlayer, useRosa } from "../../hooks/useFase2";
import { useStagioneCorrente } from "../../hooks/useStagione";
import { useSquadre } from "../../hooks/useFase7";
import { PlayerRosterRow } from "../common/PlayerRosterRow";
import { TrofeiSquadra } from "../common/TrofeiSquadra";
import { LogoSquadra } from "../common/LogoSquadra";
import { MobileShell } from "../shell/MobileShell";
import { tr } from "../../i18n";

/** Ordine canonico per il sort dei giocatori in rosa: P → D → C → A.
 * Allineato a RosaPage (Andrea 2026-05-18). */
const RUOLO_ORDER: Record<string, number> = { P: 0, D: 1, C: 2, A: 3, "?": 4 };

function ruoloClassic(p: RosaPlayer): string {
  const c = (p.ruolo_classic ?? "?").charAt(0).toUpperCase();
  return ["P", "D", "C", "A"].includes(c) ? c : "?";
}

/** Sort: prima per ruolo (P→D→C→A), poi alfabetico per nome. */
function compareByRuolo(a: RosaPlayer, b: RosaPlayer): number {
  const ra = RUOLO_ORDER[ruoloClassic(a)] ?? 99;
  const rb = RUOLO_ORDER[ruoloClassic(b)] ?? 99;
  if (ra !== rb) return ra - rb;
  return a.nome.localeCompare(b.nome, "it");
}

/** Costante speciale del select "Tutte le squadre". */
const SQUADRE_ALL = "ALL";

function PlayerRow({ p }: { p: RosaPlayer }) {
  // Andrea 2026-09-08: stessa riga di Gestione Rosa (PlayerRosterRow),
  // senza la colonna azioni/stato che qui non serve.
  return <PlayerRosterRow p={p} />;
}

function SquadraBlock({
  squadraId,
  squadraNome,
  stagioneId,
  isOwn,
}: {
  squadraId: string;
  squadraNome: string;
  stagioneId: string;
  isOwn: boolean;
}) {
  const { data: rosa, isLoading, error } = useRosa(stagioneId, squadraId);
  const [expanded, setExpanded] = useState(false);

  // Privacy: se il viewer non può vedere niente, la API ritorna privacy_visibility="nascosto"
  const isHidden = rosa?.privacy_visibility === "nascosto";

  if (isLoading) {
    return (
      <div className="card">
        <div className="font-display text-lg text-accent">{squadraNome}</div>
        <div className="text-xs text-fg-muted mt-2">{tr("Caricamento…")}</div>
      </div>
    );
  }

  if (error || isHidden) {
    return (
      <div className="card opacity-60">
        <div className="flex items-center justify-between">
          <div className="font-display text-lg text-accent">{squadraNome}</div>
          <span className="text-xs text-fg-muted">{tr("🔒 privata")}</span>
        </div>
      </div>
    );
  }

  if (!rosa) return null;

  // Andrea 2026-05-18: ordinamento per ruolo (P→D→C→A), sempre — niente
  // piu' ordinamento dipendente dall'API.
  const ufficiali = rosa.items
    .filter((p) => p.tipo_iscrizione === "ufficiale")
    .sort(compareByRuolo);
  const provvisorie = rosa.items
    .filter((p) => p.tipo_iscrizione === "provvisoria")
    .sort(compareByRuolo);

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-3 py-2 border-b border-primary/20 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <LogoSquadra squadraId={squadraId} size={32} />
          <div className="font-display text-lg text-accent">{squadraNome}</div>
          <TrofeiSquadra squadraId={squadraId} size="sm" />
          {isOwn && <span className="pill text-[10px] bg-accent/20 text-accent">{tr("tua")}</span>}
        </div>
        <div className="flex items-center gap-2 text-xs text-fg-muted">
          <span>
            <span className="text-success font-bold">{ufficiali.length}</span> {tr("uff.")}
          </span>
          {provvisorie.length > 0 && (
            <span>
              <span className="text-warning font-bold">{provvisorie.length}</span> {tr("prov.")}
            </span>
          )}
          {isOwn && (
            <Link to="/rosa" className="btn-ghost text-xs border border-accent/40 text-accent">
              {tr("Gestisci →")}
            </Link>
          )}
        </div>
      </div>

      {/* 25 ufficiali (lista competizione)
          Andrea 2026-05-22: label resa grande + verde per dare prominenza. */}
      <div className="bg-primary-deep/20">
        <div className="px-3 py-2 text-sm font-display font-bold uppercase tracking-wider text-success">
          ⚽ Lista competizione ({ufficiali.length})
        </div>
        {ufficiali.length === 0 && (
          <div className="px-3 py-3 text-xs text-fg-muted italic">
            {tr("Nessun giocatore ufficiale.")}
          </div>
        )}
        {ufficiali.map((p) => (
          <PlayerRow key={p.iscrizione_id} p={p} />
        ))}
      </div>

      {/* Extra (provvisorie) */}
      {provvisorie.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full px-3 py-2 text-left border-t border-primary/20 text-xs font-mono uppercase tracking-wider text-warning hover:bg-primary-soft/20"
          >
            {expanded ? "▼" : "▶"} Fuori Lista competizione ({provvisorie.length})
          </button>
          {expanded && (
            <div className="bg-warning/5">
              {provvisorie.map((p) => (
                <PlayerRow key={p.iscrizione_id} p={p} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function RosePage() {
  const { user } = useAuth();
  const { data: stagione, isLoading: loadingStag } = useStagioneCorrente();
  const { data: squadre, isLoading: loadingSquadre } = useSquadre();

  // Default selezione: la propria squadra se esiste, altrimenti la prima della
  // lista. Andrea 2026-05-18: niente piu' vista "tutte le squadre una sotto
  // l'altra" come default — troppo carico.
  const miaSquadra = useMemo(
    () => squadre?.find((s) => s.presidente_user_id === user?.id) ?? null,
    [squadre, user],
  );
  const defaultSquadraId = miaSquadra?.id ?? squadre?.[0]?.id ?? SQUADRE_ALL;
  const [selectedSquadraId, setSelectedSquadraId] = useState<string>("");
  // Inizializza alla prima render utile (quando squadre arrivano).
  const effectiveSquadraId = selectedSquadraId || defaultSquadraId;

  if (loadingStag || loadingSquadre) {
    return (
      <MobileShell logoSuffix="ROSE">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Rose")}</h2>
        <div className="card text-fg-muted">{tr("Caricamento…")}</div>
      </MobileShell>
    );
  }

  if (!stagione) {
    return (
      <MobileShell logoSuffix="ROSE">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Rose")}</h2>
        <div className="card text-fg-muted text-center p-8">
          <div className="text-3xl mb-2">📋</div>
          <div>{tr("Nessuna stagione attiva.")}</div>
          <div className="text-xs mt-2">
            {tr("Le rose appariranno qui quando inizierà la stagione.")}
          </div>
        </div>
      </MobileShell>
    );
  }

  const squadreList = squadre ?? [];
  // Squadre da renderizzare in base al filtro selezionato (singola o tutte).
  const squadreToShow =
    effectiveSquadraId === SQUADRE_ALL
      ? squadreList
      : squadreList.filter((s) => s.id === effectiveSquadraId);

  return (
    <MobileShell logoSuffix="ROSE">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Rose")}</h2>
        <div className="text-sm text-fg-muted">
          {tr("Stagione")} <span className="text-fg font-bold">{stagione.anno}</span>
        </div>
      </div>

      {/* Filtro squadra — default sulla propria. "Tutte" per vista completa. */}
      {squadreList.length > 1 && (
        <div className="card flex items-center gap-2 flex-wrap">
          <label className="text-xs uppercase tracking-wider text-fg-muted shrink-0">
            {tr("Squadra")}
          </label>
          <select
            value={effectiveSquadraId}
            onChange={(e) => setSelectedSquadraId(e.target.value)}
            className="flex-1 min-w-[160px] px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <option value={SQUADRE_ALL}>{tr("🌐 Tutte le squadre")}</option>
            {squadreList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
                {s.id === miaSquadra?.id ? tr(" (tua)") : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      <p className="text-xs text-fg-muted">
        {tr(
          tr(
            "Giocatori ordinati per ruolo (P→D→C→A). 25 ufficiali in alto, extra provvisori espandibili sotto.",
          ),
        )}
      </p>

      {squadreToShow.length === 0 && (
        <div className="card text-fg-muted text-center p-6">
          {tr("Nessuna squadra trovata con questo filtro.")}
        </div>
      )}

      <div className="space-y-3">
        {squadreToShow.map((s) => (
          <SquadraBlock
            key={s.id}
            squadraId={s.id}
            squadraNome={s.nome}
            stagioneId={stagione.id}
            isOwn={s.id === miaSquadra?.id}
          />
        ))}
      </div>
    </MobileShell>
  );
}
